// src/components/admin/performance/TemplateEditor.js
import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, GripVertical, Save, AlertCircle } from 'lucide-react';
import performanceService from '../../../services/performance.service';

const FIELD_TYPES = [
    { value: 'rating', label: 'Rating Scale', icon: '⭐' },
    { value: 'textarea', label: 'Long Text', icon: '📝' },
    { value: 'text', label: 'Short Text', icon: '✏️' },
    { value: 'number', label: 'Number', icon: '🔢' },
    { value: 'date', label: 'Date', icon: '📅' },
    { value: 'bulletpoints', label: 'Bullet Points', icon: '📋' }
];

const BULLET_STYLES = [
    { value: 'disc', label: '• Disc (Dot)', icon: '•' },
    { value: 'circle', label: '○ Circle', icon: '○' },
    { value: 'square', label: '▪ Square', icon: '▪' },
    { value: 'dash', label: '- Dash', icon: '-' },
    { value: 'arrow', label: '→ Arrow', icon: '→' },
    { value: 'number', label: '1. Numbered', icon: '1.' },
    { value: 'lower-alpha', label: 'a. Lowercase Letters', icon: 'a.' },
    { value: 'upper-alpha', label: 'A. Uppercase Letters', icon: 'A.' }
];

const TemplateEditor = ({ template, onClose }) => {
    const [formData, setFormData] = useState({
        template_name: '',
        description: '',
        fields: [],
        is_default: false
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [draggedIndex, setDraggedIndex] = useState(null);

    useEffect(() => {
        if (template) {
            setFormData({
                template_name: template.template_name || '',
                description: template.description || '',
                fields: Array.isArray(template.fields) ? template.fields : [],
                is_default: template.is_default || false
            });
        }
    }, [template]);

    const handleAddField = () => {
        const newField = {
            id: `field_${Date.now()}`,
            label: 'New Field',
            type: 'text',
            required: false,
            placeholder: '',
            helpText: ''
        };
        setFormData(prev => ({
            ...prev,
            fields: [...prev.fields, newField]
        }));
    };

    const handleUpdateField = (index, updates) => {
        setFormData(prev => ({
            ...prev,
            fields: prev.fields.map((field, i) =>
                i === index ? { ...field, ...updates } : field
            )
        }));
    };

    const handleRemoveField = (index) => {
        setFormData(prev => ({
            ...prev,
            fields: prev.fields.filter((_, i) => i !== index)
        }));
    };

    const handleDragStart = (index) => {
        setDraggedIndex(index);
    };

    const handleDragOver = (e, index) => {
        e.preventDefault();
        if (draggedIndex === null || draggedIndex === index) return;

        const newFields = [...formData.fields];
        const draggedField = newFields[draggedIndex];
        newFields.splice(draggedIndex, 1);
        newFields.splice(index, 0, draggedField);

        setFormData(prev => ({ ...prev, fields: newFields }));
        setDraggedIndex(index);
    };

    const handleDragEnd = () => {
        setDraggedIndex(null);
    };

    const handleSave = async () => {
        // Validation
        if (!formData.template_name.trim()) {
            setError('Template name is required');
            return;
        }

        if (formData.fields.length === 0) {
            setError('At least one field is required');
            return;
        }

        try {
            setLoading(true);
            setError(null);

            if (template) {
                await performanceService.updateTemplate(template.id, formData);
            } else {
                await performanceService.createTemplate(formData);
            }

            alert(`Template ${template ? 'updated' : 'created'} successfully!`);
            onClose(true); // true indicates refresh needed
        } catch (err) {
            console.error('Error saving template:', err);
            setError(err.response?.data?.error || 'Failed to save template');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between z-10">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                        {template ? 'Edit Template' : 'Create New Template'}
                    </h2>
                    <button
                        onClick={() => onClose(false)}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    >
                        <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6">
                    {/* Error Message */}
                    {error && (
                        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-3">
                            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                            <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
                        </div>
                    )}

                    {/* Template Info */}
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Template Name *
                            </label>
                            <input
                                type="text"
                                value={formData.template_name}
                                onChange={(e) => setFormData(prev => ({ ...prev, template_name: e.target.value }))}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                                placeholder="e.g., Annual Performance Review"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Description
                            </label>
                            <textarea
                                value={formData.description}
                                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                                rows="2"
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                                placeholder="Optional description of this template's purpose"
                            />
                        </div>

                        <div className="flex items-center">
                            <input
                                type="checkbox"
                                id="is_default"
                                checked={formData.is_default}
                                onChange={(e) => setFormData(prev => ({ ...prev, is_default: e.target.checked }))}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <label htmlFor="is_default" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                                Set as default template
                            </label>
                        </div>
                    </div>

                    {/* Fields Section */}
                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Template Fields</h3>
                            <button
                                onClick={handleAddField}
                                className="inline-flex items-center px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                            >
                                <Plus className="w-4 h-4 mr-1" />
                                Add Field
                            </button>
                        </div>

                        {formData.fields.length === 0 ? (
                            <div className="text-center py-8 bg-gray-50 dark:bg-gray-900 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600">
                                <p className="text-gray-600 dark:text-gray-400 mb-2">No fields added yet</p>
                                <button
                                    onClick={handleAddField}
                                    className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                                >
                                    Add your first field
                                </button>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {formData.fields.map((field, index) => (
                                    <FieldEditor
                                        key={field.id}
                                        field={field}
                                        index={index}
                                        onUpdate={(updates) => handleUpdateField(index, updates)}
                                        onRemove={() => handleRemoveField(index)}
                                        onDragStart={handleDragStart}
                                        onDragOver={handleDragOver}
                                        onDragEnd={handleDragEnd}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="sticky bottom-0 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
                    <button
                        onClick={() => onClose(false)}
                        className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={loading}
                        className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                    >
                        <Save className="w-4 h-4 mr-2" />
                        {loading ? 'Saving...' : 'Save Template'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// Field Editor Component
const FieldEditor = ({ field, index, onUpdate, onRemove, onDragStart, onDragOver, onDragEnd }) => {
    const [expanded, setExpanded] = useState(false);

    return (
        <div
            draggable
            onDragStart={() => onDragStart(index)}
            onDragOver={(e) => onDragOver(e, index)}
            onDragEnd={onDragEnd}
            className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-4"
        >
            <div className="flex items-center gap-3">
                <button className="cursor-move text-gray-400 hover:text-gray-600">
                    <GripVertical className="w-5 h-5" />
                </button>

                <div className="flex-1 grid grid-cols-3 gap-3">
                    <input
                        type="text"
                        value={field.label}
                        onChange={(e) => onUpdate({ label: e.target.value })}
                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white text-sm"
                        placeholder="Field Label"
                    />

                    <select
                        value={field.type}
                        onChange={(e) => onUpdate({ type: e.target.value })}
                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white text-sm"
                    >
                        {FIELD_TYPES.map(type => (
                            <option key={type.value} value={type.value}>
                                {type.icon} {type.label}
                            </option>
                        ))}
                    </select>

                    <div className="flex items-center gap-2">
                        <label className="flex items-center text-sm">
                            <input
                                type="checkbox"
                                checked={field.required}
                                onChange={(e) => onUpdate({ required: e.target.checked })}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-2"
                            />
                            <span className="text-gray-700 dark:text-gray-300">Required</span>
                        </label>
                    </div>
                </div>

                <button
                    onClick={() => setExpanded(!expanded)}
                    className="px-3 py-2 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg"
                >
                    {expanded ? 'Less' : 'More'}
                </button>

                <button
                    onClick={onRemove}
                    className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                >
                    <Trash2 className="w-4 h-4" />
                </button>
            </div>

            {/* Expanded Options */}
            {expanded && (
                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600 grid grid-cols-2 gap-3">
                    {field.type === 'rating' && (
                        <>
                            <div>
                                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Min Value</label>
                                <input
                                    type="number"
                                    value={field.min || 0}
                                    onChange={(e) => onUpdate({ min: parseFloat(e.target.value) })}
                                    className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-800 dark:text-white"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Max Value</label>
                                <input
                                    type="number"
                                    value={field.max || 5}
                                    onChange={(e) => onUpdate({ max: parseFloat(e.target.value) })}
                                    className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-800 dark:text-white"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Step</label>
                                <input
                                    type="number"
                                    step="0.1"
                                    value={field.step || 0.5}
                                    onChange={(e) => onUpdate({ step: parseFloat(e.target.value) })}
                                    className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-800 dark:text-white"
                                />
                            </div>
                        </>
                    )}
                    {field.type === 'bulletpoints' && (
                        <>
                            <div className="col-span-2">
                                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Bullet Style</label>
                                <select
                                    value={field.bulletStyle || 'disc'}
                                    onChange={(e) => onUpdate({ bulletStyle: e.target.value })}
                                    className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-800 dark:text-white"
                                >
                                    {BULLET_STYLES.map(style => (
                                        <option key={style.value} value={style.value}>
                                            {style.icon} {style.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Min Items</label>
                                <input
                                    type="number"
                                    min="1"
                                    value={field.minItems || 1}
                                    onChange={(e) => onUpdate({ minItems: parseInt(e.target.value) })}
                                    className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-800 dark:text-white"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Max Items</label>
                                <input
                                    type="number"
                                    min="1"
                                    value={field.maxItems || 10}
                                    onChange={(e) => onUpdate({ maxItems: parseInt(e.target.value) })}
                                    className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-800 dark:text-white"
                                />
                            </div>
                        </>
                    )}
                    <div className="col-span-2">
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Placeholder</label>
                        <input
                            type="text"
                            value={field.placeholder || ''}
                            onChange={(e) => onUpdate({ placeholder: e.target.value })}
                            className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-800 dark:text-white"
                        />
                    </div>
                    <div className="col-span-2">
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Help Text</label>
                        <input
                            type="text"
                            value={field.helpText || ''}
                            onChange={(e) => onUpdate({ helpText: e.target.value })}
                            className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-800 dark:text-white"
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default TemplateEditor;
